import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { denoiseImage, getTaskStatus  } from '../../services/chatApi';
import { Download, Upload, Settings, RefreshCw, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer';


const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};



const DenoiseImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();    
    const location = useLocation();
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const pollInterval = useRef(null);
    // Settings
    const [method, setMethod] = useState("Non-Local Means");
    const [strength, setStrength] = useState(10);

    // Debounce strength (Method changes should trigger immediately)
    // Debounce strength (Method changes should trigger immediately)
    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);

    const resetParams = useCallback(() => {
        setMethod("Non-Local Means");
        setStrength(10);
        setError(null);
        clearInterval(pollInterval.current);
    }, []);

    // Handle incoming image transfers
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            resetParams();
        }
    };
    // Reset strength when method changes
    const handleMethodChange = (e) => {
        const newMethod = e.target.value;
        setMethod(newMethod);
        // Set default defaults
        if (newMethod === "Non-Local Means") setStrength(10);
        else if (newMethod === "Gaussian") setStrength(1.0);
        else if (newMethod === "Median") setStrength(1); // Corresponds to kernel size 3
    };

    const handleProcess = async () => {
        if (!file) return;
        
        setIsLoading(true);
        setError(null);
        setProcessedImage(null);
        
        try {
            // Start the async task
            const startResponse = await denoiseImage(file, method, strength);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            // Poll for Completion
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        const result = statusRes.result || statusRes.info;
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                            setIsLoading(false);
                        } else {
                            setError("Invalid response format from server.");
                            setIsLoading(false);
                        }

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('denoiseImage.errors.generic'));
                        console.error("Task Failed:", statusRes);
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error while checking status.");
                }
            }, 2000); // Poll every 2 seconds

        } catch (err) {
            setError(t('denoiseImage.errors.generic'));
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = "denoised_image.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            window.open(processedImage, '_blank');
        }
    };


    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        resetParams();
    };
    // Helper to get slider props based on method
    const getSliderProps = () => {
        switch(method) {
            case "Non-Local Means": return { min: 1, max: 30, step: 1 };
            case "Gaussian": return { min: 0.1, max: 5.0, step: 0.1 };
            case "Median": return { min: 1, max: 4, step: 1 }; // 1->3, 2->5, 3->7, 4->9
            default: return { min: 0, max: 10, step: 1 };
        }
    };

    
	return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('denoiseImage.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-2xl">
                    {t('denoiseImage.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="denoise-upload" />
                        <label htmlFor="denoise-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('denoiseImage.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('denoiseImage.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Controls Column */}
                        <div className="lg:col-span-4 order-2 lg:order-1 space-y-6">
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                <div className="flex items-center gap-2 mb-4 text-brand-orange">
                                    <Settings size={20} />
                                    <h4 className="font-semibold">{t('denoiseImage.controls.title')}</h4>
                                </div>
                                
                                {/* Method Select */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('denoiseImage.controls.method')}
                                    </label>
                                    <select 
                                        value={method} 
                                        onChange={handleMethodChange}
                                        disabled={isLoading}
                                        className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-orange focus:outline-none disabled:bg-slate-100"
                                    >
                                        <option value="Non-Local Means">Non-Local Means (Best Quality)</option>
                                        <option value="Gaussian">Gaussian Blur (Fast)</option>
                                        <option value="Median">Median Blur (Despeckle)</option>
                                    </select>
                                </div>

                                {/* Strength Slider */}
                                <div className="mb-6">
                                    <div className="flex justify-between mb-2">
                                        <label className="text-sm font-medium text-slate-700">
                                            {t('denoiseImage.controls.strength')}
                                        </label>
                                        <span className="text-sm text-slate-500">{strength}</span>
                                    </div>
                                    <input 
                                        type="range" 
                                        {...getSliderProps()}
                                        value={strength} 
                                        disabled={isLoading}
                                        onChange={(e) => setStrength(parseFloat(e.target.value))} 
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange disabled:cursor-not-allowed disabled:bg-slate-300" 
                                    />
                                </div>

                                <div className="pt-4 border-t border-slate-100">
                                    {!processedImage ? (
                                        <button 
                                            onClick={handleProcess} 
                                            disabled={isLoading} 
                                            className="w-full bg-brand-orange text-white py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-70"
                                        >
                                            {isLoading ? <Loader2 className="animate-spin" /> : <Sparkles size={18} />} 
                                            {t('denoiseImage.buttons.apply')}
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            <button onClick={handleDownload} className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2">
                                                <Download size={18} /> {t('denoiseImage.buttons.download')}
                                            </button>
                                            <button onClick={() => setProcessedImage(null)} className="w-full bg-slate-200 text-slate-700 py-2 rounded-md hover:bg-slate-300 transition flex items-center justify-center gap-2">
                                                <Settings size={18} /> {t('denoiseImage.buttons.edit')}
                                            </button>
                                        </div>
                                    )}
                                    <button onClick={resetTool} className="w-full mt-3 bg-slate-100 text-slate-700 py-2 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2">
                                        <RefreshCw size={18} /> {t('denoiseImage.buttons.newImage')}
                                    </button>
                                </div>
                            </div>

                            {error && <div className="text-center p-3 bg-red-50 text-red-600 rounded-md border border-red-200 text-sm">{error}</div>}
                        </div>

                        {/* Images Column */}
                        <div className="lg:col-span-8 order-1 lg:order-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm font-semibold text-slate-500 mb-2">{t('denoiseImage.labels.original')}</p>
                                    <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                                        <img src={previewUrl} alt="Original" className="w-full h-auto" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-brand-orange mb-2">{t('denoiseImage.labels.result')}</p>
                                    <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200 min-h-[200px] flex items-center justify-center relative">
                                        {isLoading && (
                                            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                                                <Loader2 className="animate-spin text-brand-orange w-10 h-10" />
                                            </div>
                                        )}
                                        {processedImage ? (
                                            <img src={processedImage} alt="Denoised" className="w-full h-auto" />
                                        ) : (
                                            <div className="opacity-50 grayscale">
                                                <img src={previewUrl} alt="Preview" className="w-full h-auto" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {processedImage && (
                                <div className="mt-4">
                                    <ToolNavigator currentImage={processedImage} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default DenoiseImage;
